import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, FileText, Send, PenLine, Plus, ArrowRight, Clock, Newspaper } from "lucide-react";

export default function DashboardPage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('dashboard.stats.sites'),
      value: stats?.sites_count || 0,
      icon: Globe,
      color: "#1E2A5A",
      bgColor: "bg-[#1E2A5A]/10"
    },
    {
      title: t('dashboard.stats.articles'),
      value: stats?.articles_count || 0,
      icon: FileText,
      color: "#20C997",
      bgColor: "bg-[#20C997]/10"
    },
    {
      title: t('dashboard.stats.published'),
      value: stats?.published_count || 0,
      icon: Send,
      color: "#1FB6FF",
      bgColor: "bg-[#1FB6FF]/10"
    },
    {
      title: t('dashboard.stats.drafts'),
      value: stats?.drafts_count || 0,
      icon: PenLine,
      color: "#6B7280",
      bgColor: "bg-gray-100"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-pulse text-[#6B7280]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1E2A5A]">
            {t('dashboard.greeting')}, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-[#6B7280] mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" asChild className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]" data-testid="add-site-btn">
            <Link to="/sites">
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.addSite')}
            </Link>
          </Button>
          <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl" asChild data-testid="new-article-btn">
            <Link to="/articles/new">
              <PenLine className="w-4 h-4 mr-2" />
              {t('dashboard.newArticle')}
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="border-0 bg-white rounded-2xl soft-shadow hover-lift"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">{stat.title}</p>
                  <p className="text-4xl font-bold font-heading" style={{ color: stat.color }}>{stat.value}</p>
                </div>
                <div className={`w-14 h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className="w-7 h-7" style={{ color: stat.color }} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Articles & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Articles */}
        <Card className="lg:col-span-2 border-0 bg-white rounded-2xl soft-shadow" data-testid="recent-articles-card">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="font-heading text-lg text-[#1E2A5A]">{t('dashboard.recentArticles')}</CardTitle>
            <Button variant="ghost" size="sm" asChild className="text-[#20C997] hover:text-[#1E2A5A]">
              <Link to="/articles">
                {t('dashboard.viewAll')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.recent_articles?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_articles.map((article) => (
                  <Link 
                    key={article.id} 
                    to={`/articles/${article.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-[#F5F7FA] hover:bg-[#1E2A5A]/5 transition-colors group"
                    data-testid={`recent-article-${article.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#1E2A5A] truncate group-hover:text-[#20C997] transition-colors">
                        {article.title || t('articles.noArticles')}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-[#6B7280]">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(article.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Badge 
                      className={`rounded-lg ${
                        article.status === 'published' 
                          ? 'bg-[#20C997] hover:bg-[#20C997]' 
                          : 'bg-gray-200 text-[#6B7280] hover:bg-gray-200'
                      }`}
                    >
                      {article.status === 'published' ? t('articles.published') : article.status === 'draft' ? t('articles.draft') : article.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6B7280] mb-4">{t('dashboard.noArticles')}</p>
                <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl" asChild>
                  <Link to="/articles/new">{t('dashboard.createFirst')}</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 bg-white rounded-2xl soft-shadow" data-testid="quick-actions-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg text-[#1E2A5A]">{t('dashboard.quickActions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start h-14 rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#1E2A5A] hover:text-white hover:border-[#1E2A5A] transition-all" 
              asChild
              data-testid="quick-new-article-btn"
            >
              <Link to="/articles/new">
                <div className="w-10 h-10 rounded-lg bg-[#1E2A5A]/10 flex items-center justify-center mr-3">
                  <PenLine className="w-5 h-5 text-[#1E2A5A]" />
                </div>
                {t('dashboard.newArticle')}
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-14 rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#20C997] hover:text-white hover:border-[#20C997] transition-all" 
              asChild
              data-testid="quick-add-site-btn"
            >
              <Link to="/sites">
                <div className="w-10 h-10 rounded-lg bg-[#20C997]/10 flex items-center justify-center mr-3">
                  <Globe className="w-5 h-5 text-[#20C997]" />
                </div>
                {t('dashboard.addSite')}
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-14 rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#1FB6FF] hover:text-white hover:border-[#1FB6FF] transition-all" 
              asChild
              data-testid="quick-wp-posts-btn"
            >
              <Link to="/wp-posts">
                <div className="w-10 h-10 rounded-lg bg-[#1FB6FF]/10 flex items-center justify-center mr-3">
                  <Newspaper className="w-5 h-5 text-[#1FB6FF]" />
                </div>
                {t('dashboard.manageWP')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
